// src/api/httpClient.js

import axios from "axios";
import {
  getAccessToken,
  setAccessToken,
  clearAccessToken,
} from "../utils/tokenManager";
import { authAPI } from "./authApi";
import { normalizeError } from "../utils/apiErrors";

// Token refresh state management
let isRefreshing = false;
let failedQueue = [];

/**
 * Process queued requests after token refresh
 * @param {Error|null} error - Error if refresh failed, null if succeeded
 * @param {string|null} token - New access token if refresh succeeded
 */
const processQueue = (error, token = null) => {
  failedQueue.forEach((promise) => {
    if (error) {
      promise.reject(error);
    } else {
      promise.resolve(token);
    }
  });
  failedQueue = [];
};

/**
 * Create axios instance with default configuration
 *  - baseURL from environment variable
 *  - 30 second timeout
 *  - withCredentials: true (enables httpOnly cookie support)
 */
const httpClient = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  timeout: 30000,
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
  },
});

/**
 * Public endpoints that don't require authentication
 * These endpoints will not have Authorization header attached
 */
const PUBLIC_ENDPOINTS = [
  "/auth/signup",
  "/auth/login",
  "/auth/verify-code",
  "/auth/send-verification-code",
  "/auth/refresh-token",
  "/auth/forgot-password",
  "/auth/reset-password",
];

/**
 * Check if endpoint is public (doesn't need auth header)
 * @param {string} url - Request URL
 * @returns {boolean} True if endpoint is public
 */
const isPublicEndpoint = (url) => {
  return PUBLIC_ENDPOINTS.some((endpoint) => url.includes(endpoint));
};

/**
 * Request interceptor
 * Attaches Authorization header with access token to protected endpoints
 */
httpClient.interceptors.request.use(
  (config) => {
    if (config.data instanceof FormData) {
      delete config.headers["Content-Type"];
    }
    // Skip auth header for public endpoints
    if (isPublicEndpoint(config.url)) {
      return config;
    }

    // Attach access token to protected endpoints
    const token = getAccessToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

/**
 * Response interceptor
 * Basic error handling (token refresh added in Phase 2)
 */
httpClient.interceptors.response.use(
  (response) => {
    // Return response data directly
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // Handle 401 error (token expired) - but not for refresh-token endpoint
    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !originalRequest.url.includes("/auth/refresh-token") &&
      !isPublicEndpoint(originalRequest.url)
    ) {
      // If already refreshing, queue this request
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return httpClient(originalRequest);
          })
          .catch((error) => {
            return Promise.reject(error);
          });
      }

      // Mark this request as retried to prevent infinite loops
      originalRequest._retry = true;
      isRefreshing = true;

      try {
        // Attempt to refresh token using httpOnly cookie
        const response = await authAPI.refreshToken();
        const newAccessToken = response.data.accessToken;

        // Update token in memory
        setAccessToken(newAccessToken);
        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;

        // Process all queued requests with new token
        processQueue(null, newAccessToken);

        // Retry the original request
        return httpClient(originalRequest);
      } catch (refreshError) {
        // Refresh failed - clear everything and redirect to login
        processQueue(refreshError, null);
        clearAccessToken();

        // Only redirect if not already on login page
        if (window.location.pathname !== "/login") {
          window.location.href = "/login";
        }

        return Promise.reject(normalizeError(refreshError));
      } finally {
        isRefreshing = false;
      }
    }

    // Handle other errors (network, validation, etc)
    return Promise.reject(normalizeError(error));
  },
);

export default httpClient;
